import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { TextField, Autocomplete, CircularProgress } from "@mui/material";
import { useInvoiceFormContext } from "../../../../contexts/InvoiceFormContext";
import useNetsuiteLookup from "../../../../hooks/invoices/useNetsuiteLookup";

interface SubscriptionPlanOption {
  label: string;
  id: string;
}

const SubscriptionPlanAutocomplete: React.FC = () => {
  const { invoiceFormData, setInvoiceFormData } = useInvoiceFormContext();
  const fetchSubscriptionPlanData = useNetsuiteLookup("subscriptionplan");
  const [options, setOptions] = useState<SubscriptionPlanOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(invoiceFormData.subscriptionPlanName);
  const [value, setValue] = useState<SubscriptionPlanOption | null>(null);

  const throttledFetch = useCallback(
    _.throttle(async (query: string) => {
      setLoading(true);
      const data = await fetchSubscriptionPlanData(query);
      setOptions(data);
      setLoading(false);
    }, 3000),
    [fetchSubscriptionPlanData],
  );

  const debouncedInputChange = useCallback(
    _.debounce((value: string) => {
      throttledFetch(value);
    }, 1000),
    [throttledFetch],
  );

  useEffect(() => {
    if (inputValue) {
      debouncedInputChange(inputValue);
    } else {
      setOptions([]);
    }
  }, [inputValue, debouncedInputChange]);

  useEffect(() => {
    if (invoiceFormData.subscriptionPlanId && invoiceFormData.subscriptionPlanName) {
      setValue({
        label: invoiceFormData.subscriptionPlanName,
        id: invoiceFormData.subscriptionPlanId,
      });
    }
  }, [
    invoiceFormData.subscriptionPlanId,
    invoiceFormData.subscriptionPlanName,
  ]);

  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: SubscriptionPlanOption | null,
  ) => {
    if (newValue) {
      setInvoiceFormData((prev) => ({
        ...prev,
        subscriptionPlanId: newValue.id,
        subscriptionPlanName: newValue.label,
      }));
      setValue(newValue);
    } else {
      setInvoiceFormData((prev) => ({
        ...prev,
        subscriptionPlanId: "",
        subscriptionPlanName: "",
      }));
      setValue(null);
    }
  };

  return (
    <Autocomplete
      id="subscription-plan-autocomplete"
      options={options}
      getOptionLabel={(option) => option.label}
      onInputChange={handleInputChange}
      onChange={handleChange}
      inputValue={inputValue}
      value={value}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Subscription Plan"
          fullWidth
          margin="normal"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SubscriptionPlanAutocomplete;
